import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { HcToastOptions, HcToasterService } from '@healthcatalyst/cashmere';

export interface KeyElement {
    'keyID': number,
    'name': string,
    'username': string,
    'date': string,
    'value': string
}

@Injectable()
export class KeyService {
    keyList: BehaviorSubject<KeyElement[] | null> = new BehaviorSubject<KeyElement[] | null>(null);
    loadFailed = false;

    constructor(private http: HttpClient, private oktaService: OktaService, private toasterService: HcToasterService) { };

    get keyListUpdated(): Observable<KeyElement[] | null> {
        return this.keyList.asObservable();
    }

    refresh( adminList: boolean ) {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const urlStr = adminList ? '/admin.keys' : '/keys/' + this.oktaService.username.value;

        this.http.get<any>(environment.azure.url + urlStr, options)
            .subscribe({
                next: data => {
                    this.keyList.next(data);
                    this.loadFailed = false;
                },
                error: msg => {
                    this.loadFailed = true;
                    this.keyList.next(null);
                    console.log(msg);
                }
            });
    }

    add(target: KeyElement, admin: boolean) {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/keys.add', target, options).subscribe({
            next: () => {
                this.refresh(admin);
                const options: HcToastOptions = {
                    header: 'API Key Added',
                    body: target.name + " successfully added.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Add Failed',
                    body: "Unable to add key. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    update(target: KeyElement, admin: boolean) {
        // Update target in databases
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        const postSub = this.http.post<any>(environment.azure.url + '/keys.update', target, options).subscribe({
            next: () => {
                this.refresh(admin);
                const options: HcToastOptions = {
                    header: 'API Key Updated',
                    body: target.name + " successfully updated.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Update Failed',
                    body: "Unable to update key. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }

    remove(target: KeyElement, admin: boolean) {
        // Remove target from database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };
        const postSub = this.http.post<any>(environment.azure.url + '/keys.remove', target, options).subscribe({
            next: () => {
                this.refresh(admin);
                const options: HcToastOptions = {
                    header: 'API Key Removed',
                    body: target.name + " successfully removed.",
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                postSub.unsubscribe();
            },
            error: msg => {
                const options: HcToastOptions = {
                    header: 'Remove Failed',
                    body: "Unable to remove key. Try again later.",
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
                postSub.unsubscribe();
            }
        });
    }
}